import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";

// Custom SVG divIcon markers (avoids default icon URL issues with bundlers)
const buildIcon = (bg, fg, label) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${bg};color:${fg};border:3px solid #fff;box-shadow:0 4px 10px rgba(0,0,0,.18);border-radius:9999px;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-family:'Manrope',sans-serif;font-weight:800;font-size:12px;">${label}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });

const TASK_ICON = buildIcon("#A3E635", "#0A1930", "$");
const URGENT_ICON = buildIcon("#0A1930", "#A3E635", "!");
const ME_ICON = buildIcon("#0A1930", "#A3E635", "Moi");

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds.pad(0.2), { maxZoom: 13, animate: true });
  }, [points, map]);
  return null;
}

export default function TaskMap({ center, tasks, radiusKm }) {
  const mapRef = useRef(null);
  const validTasks = useMemo(
    () => (tasks || []).filter((t) => t.client_lat != null && t.client_lng != null),
    [tasks]
  );
  const points = useMemo(
    () => (center ? [center, ...validTasks.map((t) => ({ lat: t.client_lat, lng: t.client_lng }))] : validTasks.map((t) => ({ lat: t.client_lat, lng: t.client_lng }))),
    [center, validTasks]
  );
  const fallback = center || (validTasks[0] ? { lat: validTasks[0].client_lat, lng: validTasks[0].client_lng } : { lat: 45.5017, lng: -73.5673 });

  return (
    <div className="h-[420px] md:h-[520px] rounded-2xl overflow-hidden border-2 border-[#E2E8F0]" data-testid="task-map">
      <MapContainer
        ref={mapRef}
        center={[fallback.lat, fallback.lng]}
        zoom={11}
        scrollWheelZoom
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {center && (
          <>
            <Marker position={[center.lat, center.lng]} icon={ME_ICON}>
              <Popup>Ta position</Popup>
            </Marker>
            {radiusKm && (
              <Circle
                center={[center.lat, center.lng]}
                radius={radiusKm * 1000}
                pathOptions={{ color: "#0A1930", fillColor: "#A3E635", fillOpacity: 0.08, weight: 2 }}
              />
            )}
          </>
        )}
        {validTasks.map((t) => (
          <Marker
            key={t.id}
            position={[t.client_lat, t.client_lng]}
            icon={t.urgent ? URGENT_ICON : TASK_ICON}
          >
            <Popup>
              <div style={{ minWidth: 200 }}>
                <div style={{ fontFamily: "'Cabinet Grotesk',sans-serif", fontWeight: 900, color: "#0A1930", fontSize: 16 }}>
                  {t.title}
                </div>
                <div style={{ color: "#475569", fontSize: 12, marginTop: 2 }}>{t.area}</div>
                <div style={{ marginTop: 6, fontWeight: 800, color: "#0A1930" }}>
                  Tu reçois : {t.helper_subtotal.toFixed(2)} $
                </div>
                {t.distance_km != null && (
                  <div style={{ fontSize: 12, color: "#475569" }}>À {t.distance_km} km</div>
                )}
                <Link to={`/tasks/${t.id}`} style={{ display: "inline-block", marginTop: 8, background: "#A3E635", color: "#0A1930", fontWeight: 800, padding: "6px 12px", borderRadius: 999, textDecoration: "none", fontSize: 13 }}>
                  Voir la tâche →
                </Link>
              </div>
            </Popup>
          </Marker>
        ))}
        <FitBounds points={points} />
      </MapContainer>
    </div>
  );
}
